import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Form, Input, Select, Button, Typography, Space, Spin } from "antd";
import { SaveOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNotify } from "../../../context/NotificationContext";
import { useCommon } from "../../../context/CommonContext";
import announcementService from "../../../services/announcementService";
import { noWhitespaceValidator } from "../../../utils/commonFunctions";

const { Title } = Typography;

const EditAnnouncement = () => {
  const navigate = useNavigate();
  const { announcement_id } = useParams();
  const notify = useNotify();
  const { setPageTitle } = useCommon();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPageTitle("Edit Announcement");

    const load = async () => {
      if (!announcement_id) return;
      try {
        setLoading(true);
        const response = await announcementService.getAnnouncementById(
          announcement_id
        );
        // The API wraps a single row in data; some list endpoints wrap it again,
        // so both shapes are unwrapped here rather than assuming one.
        const row = response?.data?.data?.data || response?.data?.data;

        if (!response?.data?.status || !row) {
          notify.error({
            message: response?.data?.message || "Announcement not found",
          });
          navigate("/announcements");
          return;
        }

        form.setFieldsValue({
          announcement_title: row.announcement_title || "",
          announcement_subtitle: row.announcement_subtitle || "",
          announcement_name: row.announcement_name || "",
          announcement_is_active: row.announcement_is_active || "Y",
        });
      } catch (error) {
        console.error("Error loading announcement:", error);
        notify.error({
          message: error?.data?.message || "Failed to load announcement",
        });
        navigate("/announcements");
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcement_id]);

  const handleSubmit = async (values) => {
    try {
      setSaving(true);

      const payload = {
        announcement_name: values.announcement_name?.trim() || undefined,
        announcement_title: values.announcement_title?.trim(),
        announcement_subtitle: values.announcement_subtitle?.trim() || null,
        announcement_is_active: values.announcement_is_active ?? "Y",
      };

      const response = await announcementService.updateAnnouncement(
        announcement_id,
        payload
      );

      if (response?.data?.status) {
        notify.success({
          message: response.data.message || "Announcement updated successfully",
        });
        navigate("/announcements");
      } else {
        notify.error({
          message: response?.data?.message || "Failed to update announcement",
        });
      }
    } catch (error) {
      console.error("Error updating announcement:", error);
      notify.error({
        message: error?.data?.message || "Error updating announcement",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Spin spinning={loading || saving}>
      <Card>
        <Title level={4} style={{ marginTop: 0, marginBottom: 24 }}>
          Edit Announcement
        </Title>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ announcement_is_active: "Y" }}
          style={{ maxWidth: 720 }}
        >
          <Form.Item
            name="announcement_title"
            label="Title"
            rules={[
              { required: true, message: "Please enter a title" },
              { validator: noWhitespaceValidator },
            ]}
          >
            <Input placeholder="e.g. Midnight Premiere" size="large" allowClear />
          </Form.Item>

          {/* The card's whole second line, separator included — see the add page. */}
          <Form.Item name="announcement_subtitle" label="Subtitle">
            <Input
              placeholder="e.g. Shadow Who · 12 Jul"
              size="large"
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="announcement_name"
            label="Internal Name"
            rules={[
              { required: true, message: "Please enter an internal name" },
              { validator: noWhitespaceValidator },
            ]}
            extra="Only shown in the CMS, never to viewers."
          >
            <Input placeholder="e.g. July premiere card" size="large" allowClear />
          </Form.Item>

          <Form.Item name="announcement_is_active" label="Status">
            <Select
              size="large"
              options={[
                { label: "Active", value: "Y" },
                { label: "Inactive", value: "N" },
              ]}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                size="large"
              >
                Update
              </Button>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/announcements")}
                size="large"
              >
                Go Back
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </Spin>
  );
};

export default EditAnnouncement;
